import React, { useEffect } from 'react';

import { AssetDetails } from 'src/services/responseTypes/singlePlayResponse';
import { railsPropertySetter } from 'src/utils/analytics/mixpanel/mixpanelHelper';
import { getDeviceWidth } from 'src/utils/getDeviceWidth';
import {
  MixpanelRailTitle,
  MixpanelRailTypes,
  RailsMixpanelPayload
} from 'src/utils/railsMixpanelPayloadTypes';

import { ContentMetaData } from '../../../services/responseTypes/movieResponse';
import {
  ConsumptionPageTypes,
  SimilarContentGenreType
} from '../../../types/consumption';
import { GenerateThumbnailObject } from '../../../utils/ConsumptionHelper/ConsumptionHelper';
import { IMAGE_PATH_AND_NAME } from '../../../utils/ImageHelper/ImagePathHelper';
import { getImageURL } from '../../../utils/ImageHelper/ImageURLGenerator';
import ThumbnailComponent from '../../thumbnail';
import * as customStyled from './styles';
// import { upNextThumbnails } from '../../../sample-data/thumbnails/upnext-thumbnails';
import { useAppStore } from 'src/hooks/useAppStore';
import { TAG_NAME } from 'src/utils/constants/TagNameConstants';
import { ThumbnailTypes } from '../../thumbnail/types';

export interface RecommendedContentsProps {
  recommendedContents: ConsumptionPageTypes.RecommendedRail &
    SimilarContentGenreType;
  type?: ThumbnailTypes;
  sectionHeight: number;
  assetDetails: AssetDetails;
}

const RecommendedContents: React.FC<RecommendedContentsProps> = ({
  recommendedContents,
  type,
  assetDetails
}) => {
  const { companionAdState, usersPreferenceData } = useAppStore();

  const recommendedContentList =
    recommendedContents?.contents as unknown as ContentMetaData[];
  const recommendedContentTitle = recommendedContents?.title;

  const renderThumbnails = (
    thumbnails: ContentMetaData[],
    tagName: keyof typeof IMAGE_PATH_AND_NAME
  ) => {
    return thumbnails?.map((content: ContentMetaData, index: number) => {
      if (!Object.keys(content).length) return <></>;
      const generateThumbnailInstance: GenerateThumbnailObject =
        GenerateThumbnailObject.getThumbnailInstance(
          type as ThumbnailTypes,
          tagName,
          {
            content,
            hoverOut: false,
            displayTitleOnImage: false
          }
        );
      const generateThumbnailObject = generateThumbnailInstance.generate();
      const imageURL = getImageURL({
        tagName: generateThumbnailObject.tagName,
        deviceWidth: getDeviceWidth() || 0,
        item: {
          id: content.id,
          title: content.title,
          assetType: content.assetType,
          image: content.image
        }
      });
      return (
        <ThumbnailComponent
          key={generateThumbnailObject.contentId}
          {...generateThumbnailObject}
          imageUrl={imageURL}
          dataTestId={`recommended-thumbnail-${index + 1}`}
        />
      );
    });
  };

  useEffect(() => {
    const mixpanelPayload: RailsMixpanelPayload = {
      trayDetails: {
        allCollectionsResolved: false,
        index: 0,
        isEpisodeConsumption: type === ThumbnailTypes.upnext,
        isPendingResolved: true,
        isRecommendedColumn: true,
        pendingDataAvailable: false,
        title: MixpanelRailTitle.RECO,
        tabName: '',
        talamoosModelName: '',
        type: MixpanelRailTypes.RECO,
        carouselTitle: recommendedContentTitle,
        assestDetails: {
          originalTitle: assetDetails?.original_title,
          contentId: assetDetails?.id,
          assetSubtype: assetDetails?.asset_subtype,
          title: assetDetails?.title
        }
      },
      trayData: {
        content: recommendedContentList?.map((content) => ({
          title: content.title,
          value: content.title
        })),
        partnerDetails: {
          content_partner_id: usersPreferenceData?.partner
            ?.partner_id as number,
          content_partner_name: usersPreferenceData?.partner
            ?.partner_name as string
        }
      },
      activeSubscriptions: []
    };
    railsPropertySetter(mixpanelPayload);
  }, []);
  return (
    <>
      <h2
        css={customStyled.recommendedContentsTitle}
        data-testid="recommended-contents-title">
        {recommendedContentTitle}
      </h2>
      <div
        css={customStyled.recommendedContents(
          companionAdState?.companionAdLoaded &&
            companionAdState?.isAdPlayingOnPlayer
        )}>
        {renderThumbnails(
          recommendedContentList,
          type == ThumbnailTypes.vertical
            ? TAG_NAME.PORTRAIT_SMALL
            : TAG_NAME.SAME_EPISODE_LIST
        )}
      </div>
    </>
  );
};

export default React.memo(RecommendedContents);
